import env from '@config/env';

export const ROUTE_PATHS = {
  LOGIN: '/login',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  VERIFY_OTP: '/verify-otp',
  CREATE_PROFILE: '/create-profile',
  SEARCH_BUSINESS: '/search-business',
  BUSINESS_DETAIL: '/business-detail',
  REQUEST_SUBMITTED: '/request-submitted',
  DASHBOARD: '/dashboard',
  LAUNCHPAD: '/launchpad',
  SETUP_GUIDE: '/launchpad',
  FEED: '/feed',
  FEED_FOLLOWER_DETAIL: '/feed/followers',
  TEAM: '/team',
  TEAM_MEMBER_CREATE: '/team/add',
  TEAM_MEMBER_ASSIGN: '/team/add/permissions',
  TEAM_MEMBER_MANAGE: '/team/members',
  NUDGES: '/nudges',
  NUDGE_CREATE: '/nudges/create',
  NUDGE_MANAGE: '/nudges/manage',
  MANAGE_BUSINESS: '/manage-business',
  PROFILE: '/profile',
  SETTINGS: '/settings',
  USERS: '/users',
  ADMIN_USER_ADD: '/users/add',
  ADMIN_USER_DETAIL: '/users/manage',
  SAVVY_NUDGE: '/savvy-nudge',
  QUESTS: '/quests',
  QUEST_CREATE: '/quests/create',
  BUSINESS_USERS: '/business-users',
  BUSINESS_USER_ADD: '/business-users/add',
  BUSINESS_USER_DETAIL: '/business-users/:userId',
  MERCHANT_USERS: '/merchant-users',
  MERCHANT_USER_DETAIL: '/merchant-users/manage',
  MERCHANT_USER_EDIT_PROFILE: '/merchant-users/manage/edit-profile',
  CONSUMERS: '/consumers',
  CONSUMER_DETAIL: '/consumers/:userId',
  MERCHANT_BUSINESSES: '/merchant-businesses',
  MERCHANT_BUSINESS_DETAIL: '/merchant-businesses/manage',
  MERCHANT_BUSINESS_PROFILE: '/merchant-businesses/manage/profile',
  BRAND_BUSINESSES: '/brand-businesses',
  VERSION_MANAGEMENT: '/version-management',
  VERSION_MANAGEMENT_ADD: '/version-management/add',
  BUSINESS_QUERIES: '/business-queries',
  BUSINESS_QUERY_DETAIL: '/business-queries/manage',
  TOOLS: '/tools',
  RESOURCES: '/resources',
  CATEGORIES: '/categories',
  CATEGORY_ADD: '/categories/add',
  CATEGORY_EDIT: '/categories/edit',
  CATEGORY_SECTIONS: '/categories/sections',
  CATEGORY_SECTION_ADD: '/categories/sections/add',
  CATEGORY_SECTION_EDIT: '/categories/sections/edit',
  CATEGORY_SUBSECTIONS: '/categories/subsections',
  CATEGORY_SUBSECTION_ADD: '/categories/subsections/add',
  CATEGORY_SUBSECTION_EDIT: '/categories/subsections/edit',
  NOT_FOUND: '/404',
  UNAUTHORIZED: '/403',
};

export const PUBLIC_ROUTES = [
  ROUTE_PATHS.LOGIN,
  ROUTE_PATHS.FORGOT_PASSWORD,
  ROUTE_PATHS.RESET_PASSWORD,
  ROUTE_PATHS.VERIFY_OTP,
  ROUTE_PATHS.CREATE_PROFILE,
  ROUTE_PATHS.SEARCH_BUSINESS,
  ROUTE_PATHS.BUSINESS_DETAIL,
  ROUTE_PATHS.REQUEST_SUBMITTED,
];

export const ONBOARDING_ROUTES = [
  ROUTE_PATHS.VERIFY_OTP,
  ROUTE_PATHS.CREATE_PROFILE,
  ROUTE_PATHS.SEARCH_BUSINESS,
  ROUTE_PATHS.BUSINESS_DETAIL,
  ROUTE_PATHS.REQUEST_SUBMITTED,
];

export const getOnboardingEntryPath = (profileCompleted) =>
  profileCompleted ? ROUTE_PATHS.SETUP_GUIDE : ROUTE_PATHS.CREATE_PROFILE;

/** Default post-auth / home landing. Merchant always opens Launch Pad. */
export const getDefaultHomePath = () =>
  env.APP_TYPE === 'merchant' ? ROUTE_PATHS.SETUP_GUIDE : ROUTE_PATHS.DASHBOARD;

export const getManageBusinessPath = () => ROUTE_PATHS.MANAGE_BUSINESS;

export const getAdminUserDetailPath = () => ROUTE_PATHS.ADMIN_USER_DETAIL;

export const getAdminUserDetailState = (userId, adminUser = null) => ({
  userId,
  adminUser,
});

export const getBusinessUserDetailPath = (userId) => `${ROUTE_PATHS.BUSINESS_USERS}/${userId}`;

export const getMerchantUserDetailState = (userId, merchantUser = null) => ({
  userId,
  merchantUser,
});

export const getMerchantUserEditProfileState = (userId, merchantUser = null) => ({
  userId,
  merchantUser,
});

export const getConsumerDetailPath = (userId) => `${ROUTE_PATHS.CONSUMERS}/${userId}`;

export const getBusinessQueryDetailPath = () => ROUTE_PATHS.BUSINESS_QUERY_DETAIL;

export const getBusinessQueryDetailState = (claimId, claim = null) => ({
  claimId,
  claim,
});

export const getMerchantBusinessDetailPath = () => ROUTE_PATHS.MERCHANT_BUSINESS_DETAIL;

export const getMerchantBusinessProfilePath = () => ROUTE_PATHS.MERCHANT_BUSINESS_PROFILE;

export const getMerchantBusinessManageState = ({
  merchantId = '',
  restaurantId = '',
  business = null,
  activeTab = '',
} = {}) => ({
  merchantId,
  restaurantId,
  business,
  ...(activeTab ? { activeTab } : {}),
});

export const getManageBusinessProfilePath = (tab = 'basic') =>
  `${ROUTE_PATHS.MANAGE_BUSINESS}?tab=${tab}`;

export const getManageBusinessSubscriptionPath = () =>
  `${ROUTE_PATHS.MANAGE_BUSINESS}?view=subscription`;

export const getManageBusinessNavigateState = (businessId) => ({ businessId });

export const getAddNewBusinessNavigateState = () => ({ addNewBusiness: true });

export const isAddNewBusinessNavigation = (state) => Boolean(state?.addNewBusiness);

export const getFeedPageState = ({ feedTab = '', openCreatePost = false } = {}) => ({
  ...(feedTab ? { feedTab } : {}),
  ...(openCreatePost ? { openCreatePost: true } : {}),
});

export const getFeedFollowerDetailState = (followerId, restaurantId) => ({
  followerId,
  restaurantId,
});

export const getTeamMemberCreateState = ({
  countryCode,
  phoneNumber,
  phone,
  restaurantId = '',
}) => ({
  countryCode,
  phoneNumber,
  phone,
  restaurantId,
});

export const getTeamMemberAssignState = (member, restaurantId = '') => ({
  member,
  restaurantId,
});

export const getTeamMemberManageState = (userId, restaurantId) => ({
  userId,
  restaurantId,
});

export const getNudgeManageState = (nudge, restaurantId = '') => ({
  nudgeId: nudge?.id || '',
  restaurantId,
});

export const getNudgeEditState = (nudgeId, restaurantId = '') => ({
  nudgeId: nudgeId || '',
  restaurantId,
  mode: 'edit',
});

export const getCategoryDetailState = (categoryId, category = null) => ({
  categoryId,
  category,
});

export const getCategoryFormState = (categoryId, category = null) => ({
  categoryId,
  category,
});

export const getCategorySectionDetailState = (
  categoryId,
  category = null,
  sectionId = '',
  section = null,
) => ({
  categoryId,
  category,
  sectionId,
  section,
});

export const getCategorySectionFormState = (
  categoryId,
  category = null,
  sectionId = '',
  section = null,
) => ({
  categoryId,
  category,
  sectionId,
  section,
});

export const getCategorySubsectionFormState = (
  categoryId,
  category = null,
  sectionId = '',
  section = null,
  subsectionId = '',
  subsection = null,
) => ({
  categoryId,
  category,
  sectionId,
  section,
  subsectionId,
  subsection,
});

export default ROUTE_PATHS;
